export type Point = { x: number; y: number };
export type Box = { left: number; top: number; right: number; bottom: number };

export function boxBetween(a: Point, b: Point): Box {
  return {
    left: Math.min(a.x, b.x),
    top: Math.min(a.y, b.y),
    right: Math.max(a.x, b.x),
    bottom: Math.max(a.y, b.y)
  };
}

/** Touching edges count: a rectangle drawn up to an item's border picks it. */
export function boxesMeet(a: Box, b: Box): boolean {
  return (
    a.left <= b.right &&
    b.left <= a.right &&
    a.top <= b.bottom &&
    b.top <= a.bottom
  );
}

export type MarqueeMode = 'replace' | 'add' | 'toggle';

/** Cmd or Ctrl flips each covered item, Shift adds the covered ones, a plain drag replaces. */
export function marqueeModeOf(keys: {
  shiftKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
}): MarqueeMode {
  if (keys.ctrlKey || keys.metaKey) return 'toggle';
  if (keys.shiftKey) return 'add';
  return 'replace';
}

export function marqueeSelection(
  mode: MarqueeMode,
  initial: string[],
  covered: string[]
): string[] {
  switch (mode) {
    case 'replace':
      return covered;
    case 'add':
      return [...new Set([...initial, ...covered])];
    case 'toggle': {
      const coveredSet = new Set(covered);
      const initialSet = new Set(initial);
      return [
        ...initial.filter(id => !coveredSet.has(id)),
        ...covered.filter(id => !initialSet.has(id))
      ];
    }
  }
}

const EDGE_ZONE = 40;
const SPEED_AT_EDGE = 15;
const MAX_SPEED = 40;

/**
 * Autoscroll along one axis, in pixels per frame: none away from the box's edges, growing as the
 * pointer goes deeper into the zone by an edge and on past it. Negative scrolls back.
 */
export function edgeScrollSpeed(
  position: number,
  start: number,
  end: number
): number {
  const zone = Math.min(EDGE_ZONE, (end - start) / 4);
  if (zone <= 0) return 0;
  const speed = (depth: number) =>
    Math.min(MAX_SPEED, Math.ceil((depth / zone) * SPEED_AT_EDGE));
  if (position < start + zone) return -speed(start + zone - position);
  if (position > end - zone) return speed(position - (end - zone));
  return 0;
}
